/**
 * Il guscio condiviso degli agenti: linguetta, intestazione e azioni.
 *
 * Nasce da un'osservazione di Enrico (16-08-2026): *«graficamente Iris è diversa
 * da Aida sia come linguetta che come intestazione»*. La causa non era lo stile
 * ma la struttura: erano due componenti diversi, e ognuno si era portato la
 * propria veste. Dal 16-08 il componente è uno solo (`ChatAgente`) e questi
 * sono i suoi pezzi.
 *
 * Da qui la veste è una sola, e ogni agente è una configurazione:
 *  - **la veste** viene da Aida (linguetta sul bordo, quadratino sfumato, stato);
 *  - **le azioni** vengono dal pannello di Iris (nuova conversazione, storico —
 *    che ad Aida mancava — e lettura vocale).
 *
 * Il **comportamento** invece resta per contesto, come deciso: dove la tabella è
 * larga il pannello scorre sopra, dove c'è spazio spinge il contenuto. Nel #37 i
 * 420px del dock non stavano insieme alle 11 colonne del tabellone, ed è la
 * ragione per cui quella pagina è finita a piena larghezza.
 */
import {
  History,
  PanelRightClose,
  Sparkles,
  Square,
  SquarePen,
  Volume2,
  VolumeX,
} from "lucide-react";

import { cn } from "@/lib/utils";

/** Larghezza del pannello aperto, condivisa: scritta a mano in due posti divergeva. */
export const AGENTE_DOCK_W = 420;

/**
 * La linguetta che apre il pannello.
 *
 * `modo="inline"` è quella di Aida, alta quanto il contenitore e integrata nel
 * bordo della pagina; `modo="flottante"` sta sul bordo della finestra e serve
 * dove il pannello arriva in overlay.
 */
export function LinguettaAgente({
  nome,
  onApri,
  modo = "inline",
  titolo,
  inerte = false,
}: {
  nome: string;
  onApri: () => void;
  modo?: "inline" | "flottante";
  titolo?: string;
  /**
   * L'agente è previsto ma non esiste ancora: la linguetta occupa il suo posto
   * senza aprire niente. Serve a **non far cambiare forma alla pagina** il giorno
   * in cui l'agente arriva — e a dire che è in arrivo, invece di lasciare un
   * bordo vuoto che nessuno saprebbe leggere.
   */
  inerte?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={inerte ? undefined : onApri}
      disabled={inerte}
      title={titolo ?? (inerte ? `${nome} — in arrivo` : `Apri ${nome}`)}
      className={cn(
        "flex flex-none flex-col items-center gap-3 border-l border-border bg-card py-4 text-brand-700 transition-colors",
        inerte
          ? "cursor-default text-muted-foreground"
          : "hover:bg-brand-50 dark:hover:bg-brand-950/40",
        modo === "inline"
          ? "h-full w-11"
          : "fixed right-0 top-1/4 z-30 w-11 rounded-l-lg border-y border-border shadow-md"
      )}
    >
      <span
        className={cn(
          "flex size-7 items-center justify-center rounded-lg",
          inerte
            ? "border border-dashed border-border bg-muted text-muted-foreground"
            : "bg-gradient-to-br from-brand-600 to-brand-800 text-white"
        )}
      >
        <Sparkles className="size-4" />
      </span>
      <span
        className="text-[11px] font-semibold tracking-wide text-muted-foreground"
        style={{ writingMode: "vertical-rl" }}
      >
        {inerte ? `${nome} · in arrivo` : nome}
      </span>
    </button>
  );
}

/** I tre pulsanti in testa al pannello: sono la parte che ad Aida mancava. */
export function AzioniAgente({
  onNuova,
  storicoAperto,
  onToggleStorico,
  voceAttiva,
  parlando,
  onToggleVoce,
  onFermaVoce,
  onChiudi,
}: {
  onNuova: () => void;
  storicoAperto: boolean;
  onToggleStorico: () => void;
  voceAttiva: boolean;
  parlando: boolean;
  onToggleVoce: () => void;
  onFermaVoce: () => void;
  /** Presente solo nella sidebar in-page, che si comprime invece di chiudersi. */
  onChiudi?: () => void;
}) {
  const bottone =
    "rounded-md p-1.5 transition-colors hover:bg-muted text-muted-foreground hover:text-foreground";
  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        onClick={onNuova}
        className={bottone}
        aria-label="Nuova conversazione"
        title="Nuova conversazione"
      >
        <SquarePen className="size-4" />
      </button>
      <button
        type="button"
        onClick={onToggleStorico}
        aria-label="Storico conversazioni"
        aria-pressed={storicoAperto}
        title="Storico conversazioni"
        className={cn(bottone, storicoAperto && "text-brand-700 dark:text-brand-400")}
      >
        <History className="size-4" />
      </button>
      {parlando ? (
        <button
          type="button"
          onClick={onFermaVoce}
          className={cn(bottone, "text-brand-700 dark:text-brand-400")}
          aria-label="Ferma la lettura"
          title="Ferma la lettura"
        >
          <Square className="size-4 fill-current" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onToggleVoce}
          aria-label={voceAttiva ? "Disattiva lettura vocale" : "Attiva lettura vocale"}
          aria-pressed={voceAttiva}
          title={
            voceAttiva
              ? "Lettura vocale attiva: legge le risposte ad alta voce"
              : "Lettura vocale disattivata"
          }
          className={cn(bottone, voceAttiva && "text-brand-700 dark:text-brand-400")}
        >
          {voceAttiva ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
        </button>
      )}
      {onChiudi && (
        <button
          type="button"
          onClick={onChiudi}
          className={bottone}
          aria-label="Comprimi"
          title="Comprimi"
        >
          <PanelRightClose className="size-4" />
        </button>
      )}
    </div>
  );
}

/**
 * L'intestazione: quadratino sfumato, nome, riga di stato, e le azioni a destra.
 * È la veste di Aida, quella che Enrico ha scelto di tenere.
 */
export function HeaderAgente({
  nome,
  stato,
  attivo = true,
  azioni,
  titoloComponente: Titolo,
}: {
  nome: string;
  /** Riga sotto il nome: cosa fa questo agente. */
  stato: string;
  attivo?: boolean;
  azioni?: React.ReactNode;
  /** Per il pannello in overlay, che deve usare `Dialog.Title` come titolo. */
  titoloComponente?: React.ComponentType<{ className?: string; children: React.ReactNode }>;
}) {
  const nomeReso = Titolo ? (
    <Titolo className="text-sm font-bold">{nome}</Titolo>
  ) : (
    <div className="text-sm font-bold">{nome}</div>
  );

  return (
    <header className="flex items-center gap-2.5 border-b border-border px-4 py-3">
      <span className="flex size-7 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-brand-600 to-brand-800 text-white">
        <Sparkles className="size-4" />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        {nomeReso}
        <div
          className={cn(
            "flex items-center gap-1.5 text-[11px] font-semibold",
            attivo ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
          )}
        >
          <span
            className={cn(
              "size-1.5 rounded-full",
              attivo ? "bg-emerald-500" : "bg-muted-foreground/50"
            )}
          />
          <span className="truncate">{stato}</span>
        </div>
      </div>
      {azioni}
    </header>
  );
}

export interface ConversazionePassata {
  id: string;
  titolo: string | null;
  creato_il: string;
  n_messaggi?: number;
  ultimo_messaggio_il?: string | null;
}

/**
 * Lo storico delle conversazioni: la funzione che Enrico ha segnalato come
 * mancante in Aida. Gli endpoint c'erano già su tutti gli agenti — mancava solo
 * chi li chiamasse.
 */
export function StoricoConversazioni({
  righe,
  caricamento,
  onApri,
  attivaId,
}: {
  righe: ConversazionePassata[];
  caricamento?: boolean;
  onApri: (id: string) => void;
  attivaId?: string | null;
}) {
  if (caricamento) {
    return <p className="p-4 text-xs text-muted-foreground">Carico lo storico…</p>;
  }
  if (!righe.length) {
    return (
      <p className="p-4 text-xs text-muted-foreground">
        Nessuna conversazione precedente. Quelle che farai restano qui.
      </p>
    );
  }
  return (
    <ul className="divide-y">
      {righe.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => onApri(c.id)}
            className={cn(
              "flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left transition-colors hover:bg-accent/50",
              attivaId === c.id && "bg-brand-50/60 dark:bg-brand-950/40"
            )}
          >
            <span className="line-clamp-1 text-[13px] font-medium">
              {c.titolo ?? "Conversazione"}
            </span>
            <span className="text-[10.5px] text-muted-foreground">
              {new Date(c.ultimo_messaggio_il ?? c.creato_il).toLocaleString("it-IT", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {c.n_messaggi ? ` · ${c.n_messaggi} messaggi` : ""}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
